"use client";
import { StudioProvider, useStudio } from "../../hooks/useStudio";
import { useEffect, useState } from "react";
import { cn, imageToDataUri } from "../../lib/utils";
import {
  ConfiguratorProvider,
  useConfiguratorContext,
} from "@studio3dx/legacy-use-configurator/react";
import { ConfiguratorConfig } from "../../hooks/configurator/types";
import SelectionHelperDialog from "./SelectionHelperDialog";
import { ConfiguratorHeader } from "./ConfiguratorHeader";
import { LoadingScreen } from "./LoadingScreen";
import { ViewerControls } from "./ViewerControls";
import { ConfigurationPanel } from "./ConfigurationPanel";
import { useIsScreenWidth } from "../../hooks/useScreenWidth";
import { ChevronDown, SlidersHorizontal } from "lucide-react";

interface ConfiguratorProps {
  modelName: string;
  config: ConfiguratorConfig;
}

function Configurator(props: ConfiguratorProps) {
  const { modelName, config } = props;

  const studio = useStudio({
    baseUrl: "https://meet.studio3dx.com/v2/scene",
    modelId: config.studioSceneId,
  });

  const [loaded, setLoaded] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isRendering, setIsRendering] = useState(false);
  const [isOptionsCollapsed, setIsOptionsCollapsed] = useState(false);

  const configurator = useConfiguratorContext();

  // Define breakpoints
  const MOBILE_BREAKPOINT = 768;
  const TABLET_BREAKPOINT = 1024;

  // Use the custom hook to check screen width
  const isMobile = useIsScreenWidth(MOBILE_BREAKPOINT);
  const isTablet = useIsScreenWidth(TABLET_BREAKPOINT) && !isMobile;
  const isMobileOrTablet = isMobile || isTablet;

  const defaultCameraTransform = isMobile
    ? config.defaultMobileCameraTransform
    : config.defaultCameraTransform;

  const resetCamera = () => {
    if (studio.loaded && defaultCameraTransform?.length === 6) {
      const [x, y, z, tx, ty, tz] = defaultCameraTransform;
      void studio.viewer.setCameraLookAt(x, y, z, tx, ty, tz, true);
    }
  };

  // Simulate loading progress
  useEffect(() => {
    if (!loaded && !studio.loaded) {
      const interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const newProgress = prev + Math.random() * 5;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 200);

      return () => clearInterval(interval);
    }

    if (studio.loaded && loadingProgress < 100) {
      setLoadingProgress(100);
    }
  }, [loaded, studio.loaded, loadingProgress]);

  const updateVisibility = () => {
    if (!studio.loaded) return;

    // Object to store visibility states for batch processing
    const visibilityUpdates: { [key: string]: boolean } = {};

    // First, hide all objects
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        const ids = [option?.id];
        if (option.metadata?.viewerObjects)
          ids.push(...option.metadata.viewerObjects);

        for (const object of ids) {
          visibilityUpdates[object] = false; // Mark object as hidden initially
        }
      }
    }

    // Then, show only enabled options
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        const selected = configurator.isOptionSelected(option.id);
        if (!selected) continue;

        const ids = [option?.id];
        if (option.metadata?.viewerObjects)
          ids.push(...option.metadata.viewerObjects);

        for (const object of ids) {
          visibilityUpdates[object] = true; // Mark object as visible if selected
        }

        // Hide specific objects if needed for selected options
        if (option?.metadata?.hideViewerObjects) {
          for (const object of option.metadata.hideViewerObjects) {
            visibilityUpdates[object] = false; // Mark object as hidden
          }
        }

        // Update materials for selected options
        if (option?.metadata?.viewerMaterials) {
          for (const [name, config] of Object.entries(
            option.metadata.viewerMaterials,
          ) as any) {
            const materialConfig = {
              ...config,
              map: config.map ? studio.getTexture(config.map) : undefined,
            };

            // Handle special cases
            // TODO: find a better way to do this
            if (option?.metadata?.customColourSourceSectionId) {
              for (const section of configurator.getAllSections()) {
                if (
                  section.id === option?.metadata?.customColourSourceSectionId
                ) {
                  const selectedOptions = configurator.getSelectedOptions(
                    section.id,
                  );

                  if (selectedOptions.length > 0) {
                    const matchOption = selectedOptions[0];
                    if (matchOption?.metadata?.viewerMaterials) {
                      for (const [name, config] of Object.entries(
                        matchOption.metadata.viewerMaterials,
                      ) as any) {
                        if (
                          name ===
                          option?.metadata?.customColourSourceMaterialName
                        ) {
                          materialConfig.color = config.color;
                        }
                      }
                    }
                  }
                }
              }
            }

            // Apply material
            studio.viewer.setMaterial(name, materialConfig);
          }
        }
      }
    }

    // Apply all visibility updates in a single batch call
    studio.viewer.setObjectVisibility(visibilityUpdates);

    // Update shadows once after all visibility and material changes
    studio.viewer.updateShadows();
  };

  useEffect(() => {
    void updateVisibility();

    // Calculate total price
    let price = 0;
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        if (configurator.isOptionSelected(option.id) && option.price) {
          price += option.price;
        }
      }
    }
    setTotalPrice(price);
  }, [configurator.selectedOptions]);

  useEffect(() => {
    if (!loaded && studio.loaded) {
      const init = async () => {
        // Load textures
        await Promise.all(
          Object.entries(config.textures).map(async ([name, uri]) => {
            const dataUrl = (await imageToDataUri(uri)) as string;
            await studio.addTexture(name, dataUrl);
          }),
        );

        // Load materials
        if (config.materials) {
          for (const [name, material] of Object.entries(
            config.materials,
          ) as any) {
            const materialConfig = {
              ...material,
              map: material.map
                ? await studio.getTexture(material.map)
                : undefined,
            };
            await studio.viewer.setMaterial(name, materialConfig);
          }
        }

        await studio.viewer.setCameraConfig({
          maxDistance: 30,
          minDistance: 8,
          maxPolarAngle: Math.PI / 2 - 0.1,
          truckSpeed: 0,
          fov: 30,
        });

        if (defaultCameraTransform.length === 6) {
          const [x, y, z, tx, ty, tz] = defaultCameraTransform;
          await studio.viewer.setCameraLookAt(x, y, z, tx, ty, tz, true);
        }
        await studio.viewer.setMSAASamples(4);
        await studio.viewer.setSMAAEnabled(true);

        await updateVisibility();
      };

      init().then(() => {
        // Start fade-out animation
        setFadeOut(true);
        // Wait for animation to complete before setting loaded to true
        setTimeout(() => {
          setLoaded(true);
        }, 500); // 500ms matches the CSS transition duration
      });
    }
  }, [loaded, studio.loaded]);

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const takeScreenshot = async () => {
    if (!loaded || !studio.viewer || isRendering) return;

    setIsRendering(true);

    try {
      // Get current camera position
      const camera = (await studio.viewer.getCamera()) as {
        position: unknown;
        target: unknown;
      };

      // Responsive render parameters - lower resolution on mobile to prevent memory issues
      const renderParams = {
        width: isMobile ? 1920 : 4160, // Mobile: Full HD (1920x1080), Desktop: 4K (4160x2340)
        height: isMobile ? 1080 : 2340, // 16:9 aspect ratio maintained on both
        fov: 30,
        format: "image/jpeg" as const,
        quality: isMobile ? 0.8 : 0.9, // Lower quality on mobile for smaller file size
        transparent: false,
        fit: false,
        position: camera.position,
        target: camera.target,
      };

      // Render the image
      const result = (await studio.viewer.renderOffscreenFrame(
        renderParams,
      )) as Blob;

      // Create download link
      const url = URL.createObjectURL(result);
      const timestamp = new Date();

      // Create a clean filename with better timestamp formatting
      const modelDisplayName = modelName.toLowerCase().replace(/\s+/g, "-");

      // Format timestamp as YYYY-MM-DD_HH-MM-SS for better readability
      const year = timestamp.getFullYear();
      const month = String(timestamp.getMonth() + 1).padStart(2, "0");
      const day = String(timestamp.getDate()).padStart(2, "0");
      const hours = String(timestamp.getHours()).padStart(2, "0");
      const minutes = String(timestamp.getMinutes()).padStart(2, "0");
      const seconds = String(timestamp.getSeconds()).padStart(2, "0");

      const timestampStr = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      const filename = `caroffshore-${modelDisplayName}-${timestampStr}.jpg`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Screenshot failed:", error);
    } finally {
      setIsRendering(false);
    }
  };

  return (
    <>
      <main className="flex flex-col w-screen h-dvh min-h-0 select-none bg-linear-to-br from-gray-50 to-white">
        <SelectionHelperDialog />
        <ConfiguratorHeader
          isMobile={isMobile}
          isMobileOrTablet={isMobileOrTablet}
        />

        <div
          className={cn(
            "flex flex-1 min-h-0 w-full overflow-hidden relative",
            isMobileOrTablet ? "flex-col" : "flex-row",
            fullScreen && "flex-col",
          )}
        >
          {/* Viewer */}
          <div
            className={cn(
              "relative z-10 flex flex-col bg-white overflow-hidden min-h-0",
              isMobileOrTablet
                ? isOptionsCollapsed
                  ? "flex-1 min-h-0 w-full"
                  : "basis-1/2 min-h-0 w-full shrink-0"
                : "flex-1 min-h-0",
              fullScreen && "flex-1",
            )}
          >
            <div className="overflow-hidden h-full flex flex-1 min-h-0 relative portrait:rounded-none">
              <LoadingScreen isVisible={!loaded} progress={loadingProgress} />

              {isRendering && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-2xl z-30 flex items-center justify-center transition-opacity duration-300">
                  <div className="bg-white rounded-lg px-6 py-4 shadow-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                      <span className="text-gray-700 font-medium">
                        Taking Screenshot...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <ViewerControls
                fullScreen={fullScreen}
                onToggleFullScreen={toggleFullScreen}
                onTakeScreenshot={takeScreenshot}
                onResetCamera={resetCamera}
                isRendering={isRendering}
                isMobile={isMobile}
              />

              {loaded && isMobileOrTablet && (
                <button
                  type="button"
                  onClick={() => setIsOptionsCollapsed(!isOptionsCollapsed)}
                  className={cn(
                    "absolute z-[35] pointer-events-auto",
                    "flex h-11 w-11 items-center justify-center rounded-full",
                    "bg-white/95 backdrop-blur-md border-2 border-gray-200/60",
                    "shadow-lg text-gray-800 hover:bg-white hover:border-gray-300 hover:shadow-xl",
                    "transition-all duration-200 active:scale-95",
                    "left-[max(0.75rem,env(safe-area-inset-left))]",
                    "bottom-[max(0.75rem,env(safe-area-inset-bottom))]",
                  )}
                  aria-expanded={!isOptionsCollapsed}
                  aria-label={
                    isOptionsCollapsed
                      ? "Open configuration"
                      : "Hide configuration"
                  }
                >
                  {isOptionsCollapsed ? (
                    <SlidersHorizontal
                      className="h-[1.125rem] w-[1.125rem] stroke-[2]"
                      aria-hidden
                    />
                  ) : (
                    <ChevronDown
                      className="h-[1.25rem] w-[1.25rem] stroke-[2]"
                      aria-hidden
                    />
                  )}
                </button>
              )}

              <iframe
                ref={studio.iframeRef}
                className="absolute inset-0 min-h-full w-full border-0"
              />
            </div>
          </div>

          {/* Configuration panel: wrapper only for split layout; desktop stays a direct flex child so basis-* is relative to the row */}
          {!fullScreen &&
            (isMobileOrTablet ? (
              <div
                className={cn(
                  "relative min-h-0 shrink-0",
                  isOptionsCollapsed
                    ? "m-0 h-0 w-full min-h-0 overflow-hidden border-0 p-0"
                    : "flex w-full basis-1/2 flex-col overflow-hidden",
                )}
              >
                <ConfigurationPanel
                  config={config}
                  fullScreen={false}
                  totalPrice={totalPrice}
                  isMobileDrawer
                />
              </div>
            ) : (
              <ConfigurationPanel
                config={config}
                fullScreen={false}
                totalPrice={totalPrice}
              />
            ))}
        </div>
      </main>
    </>
  );
}

const ConfiguratorWrapper = (props: ConfiguratorProps) => (
  <StudioProvider>
    <ConfiguratorProvider
      sections={props.config.sections}
      configuratorName={props.modelName}
    >
      <Configurator {...props} />
    </ConfiguratorProvider>
  </StudioProvider>
);

export default ConfiguratorWrapper;
